// src/components/InvoicePopup.jsx
import React, { useEffect, useState } from "react";
import {
  doc, getDoc, collection, query, where, getDocs, updateDoc, addDoc
} from "firebase/firestore";
import { db } from "../firebase";

export default function InvoicePopup({ invoiceId, onClose }) {
  const [editable, setEditable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [parties, setParties] = useState([]);
  const [originalPartyPresent, setOriginalPartyPresent] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // new states for barcode lookup UI
  const [barcodeInput, setBarcodeInput] = useState("");
  // local state for IGST toggle & payment (mirrors editable when loaded)
  const [isInterState, setIsInterState] = useState(false);
  const [payment, setPayment] = useState({ mode: "Cash", amountPaid: 0, details: {} });

  // ------------------ Fetch Logic ------------------
  useEffect(() => {
    if (!invoiceId) return;
    let mounted = true;

    const fetchParties = async () => {
      try {
        const snaps = await getDocs(collection(db, "parties"));
        const list = snaps.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (mounted) setParties(list);
      } catch (e) {
        console.error("fetchParties error:", e);
      }
    };

    const fetchPartyFromRefOrId = async (raw) => {
      try {
        if (raw.party && String(raw.party).trim() !== "") return String(raw.party);
        if (raw.partyName && String(raw.partyName).trim() !== "") return String(raw.partyName);
        if (raw.supplier && String(raw.supplier).trim() !== "") return String(raw.supplier);

        if (raw.partyRef) {
          try {
            const psnap = await getDoc(raw.partyRef);
            if (psnap.exists()) {
              const pdata = psnap.data();
              return pdata.partyName || pdata.name || "";
            }
          } catch {}
        }

        if (raw.partyId) {
          try {
            const psnap = await getDoc(doc(db, "parties", raw.partyId));
            if (psnap.exists()) {
              const pdata = psnap.data();
              return pdata.partyName || pdata.name || "";
            }
          } catch {}
        }

        if (raw.partyCode) {
          try {
            const q = query(collection(db, "parties"), where("code", "==", raw.partyCode));
            const snaps = await getDocs(q);
            if (!snaps.empty) {
              const pdata = snaps.docs[0].data();
              return pdata.partyName || pdata.name || "";
            }
          } catch {}
        }
      } catch {}
      return "";
    };

    const normalizeDateToISO = (d) => {
      if (!d && d !== 0) return "";
      if (typeof d === "object" && d !== null && typeof d.toDate === "function") {
        return d.toDate().toISOString().slice(0, 10);
      }
      try {
        const dt = new Date(d);
        if (isNaN(dt.getTime())) return "";
        return dt.toISOString().slice(0, 10);
      } catch { return ""; }
    };

    const fetchInvoice = async () => {
      setLoading(true);
      setError(null);
      setEditable(null);
      setOriginalPartyPresent(false);

      try {
        let raw;
        const { loadSalesImportInvoiceLikeDocs } = await import("../services/salesImportTransactionDataService");
        const docs = await loadSalesImportInvoiceLikeDocs({ maxWeeks: 24 });
        raw = docs.find(
          (d) => d.id === invoiceId || String(d.voucherNo || "") === String(invoiceId || "")
        );
        if (!raw) {
          if (mounted) {
            setError("Voucher not found in sales import");
            setLoading(false);
          }
          return;
        }
        const originalPartyField = raw.party || raw.partyName;
        setOriginalPartyPresent(Boolean(originalPartyField && String(originalPartyField).trim() !== ""));

        raw.date = normalizeDateToISO(raw.date);
        raw.items = Array.isArray(raw.items) ? raw.items.map((it) => ({
          code: it.code || it.itemCode || "",
          itemName: it.itemName || it.name || it.desc || "",
          desc: it.desc || it.description || "",
          qty: (it.qty === "" || it.qty === undefined) ? 0 : it.qty,
          rate: (it.rate === "" || it.rate === undefined) ? 0 : it.rate,
          discount: (it.discount === "" || it.discount === undefined) ? 0 : it.discount,
          GST: (it.GST === "" || it.GST === undefined) ? 12 : it.GST,
          ...(it || {}),
        })) : [];

        const fetchedPartyName = await fetchPartyFromRefOrId(raw);
        if (!originalPartyPresent && fetchedPartyName) raw.party = fetchedPartyName;
        else raw.party = raw.party ?? "";

        // set IGST and payment local states from raw (if present)
        if (mounted) {
          setEditable(JSON.parse(JSON.stringify(raw)));
          setIsInterState(Boolean(raw.isInterState));
          setPayment(raw.payment ? raw.payment : { mode: "Cash", amountPaid: 0, details: {} });
        }
      } catch (err) {
        console.error("fetchInvoice error", err);
        if (mounted) setError("Error loading invoice");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchParties();
    fetchInvoice();

    return () => { mounted = false; };
  }, [invoiceId]);

  // ------------------ Helpers ------------------
  const calcRow = (row) => {
    // careful numeric conversions
    const qty = Number(row.qty || 0);
    const rate = Number(row.rate || 0);
    const discPct = Number(row.discount || 0);
    const gstPct = Number(row.GST || 0);

    // compute gross digit-by-digit
    const gross = +(qty * rate).toFixed(2);
    const discAmt = +((gross * discPct) / 100).toFixed(2);
    const taxable = +(gross - discAmt).toFixed(2);

    if (isInterState) {
      const igstAmt = +((taxable * gstPct) / 100).toFixed(2);
      const netAmount = +((taxable + igstAmt).toFixed(2));
      return { gross, discAmt, taxable, sgstAmt: 0, cgstAmt: 0, igstAmt, netAmount };
    } else {
      const halfPct = gstPct / 2;
      const sgstAmt = +((taxable * halfPct) / 100).toFixed(2);
      const cgstAmt = +((taxable * halfPct) / 100).toFixed(2);
      // adjust rounding so sum matches
      const gstSum = +(sgstAmt + cgstAmt).toFixed(2);
      const netAmount = +((taxable + gstSum).toFixed(2));
      return { gross, discAmt, taxable, sgstAmt, cgstAmt, igstAmt: 0, netAmount };
    }
  };

  const totals = (editable?.items || []).reduce((acc, row) => {
    const { discAmt, sgstAmt = 0, cgstAmt = 0, igstAmt = 0, netAmount } = calcRow(row);
    acc.qty += Number(row.qty || 0);
    acc.gross += Number(row.rate || 0) * Number(row.qty || 0);
    acc.discount += discAmt;
    acc.sgst += sgstAmt;
    acc.cgst += cgstAmt;
    acc.igst += igstAmt;
    acc.net += netAmount;
    // keep two decimals
    acc.gross = +acc.gross.toFixed(2);
    acc.discount = +acc.discount.toFixed(2);
    acc.sgst = +acc.sgst.toFixed(2);
    acc.cgst = +acc.cgst.toFixed(2);
    acc.igst = +acc.igst.toFixed(2);
    acc.net = +acc.net.toFixed(2);
    return acc;
  }, { qty: 0, gross: 0, discount: 0, sgst: 0, cgst: 0, igst: 0, net: 0 });

  const grandTotalRounded = Math.round(totals.net || 0);
  const roundOff = Number(((grandTotalRounded || 0) - (totals.net || 0)).toFixed(2));
  const grandTotalDisplay = (grandTotalRounded || 0).toFixed(2);

  // ------------------ Keyboard Delete ------------------
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedRow !== null && e.key === "Delete" && (e.ctrlKey || e.metaKey)) {
        setEditable((prev) => {
          const items = [...prev.items];
          items.splice(selectedRow, 1);
          return { ...prev, items };
        });
        setSelectedRow(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedRow]);

  // ------------------ Handlers ------------------
  const handleInvoiceFieldChange = (field, value) => {
    setEditable((prev) => ({ ...prev, [field]: value }));
  };

  const handleItemChange = (idx, field, value) => {
    setEditable((prev) => {
      const items = [...(prev?.items || [])];
      const item = { ...(items[idx] || {}) };
      if (["qty","rate","discount","GST"].includes(field)) {
        item[field] = (value === "" ? "" : Number(value));
      } else item[field] = value;
      items[idx] = item;
      return { ...prev, items };
    });
  };

  const addItemToEditable = (itemObj) => {
    setEditable((prev) => {
      const items = Array.isArray(prev.items) ? [...prev.items] : [];
      items.push({
        code: itemObj.code || itemObj.barcode || "",
        desc: itemObj.desc || itemObj.itemName || itemObj.name || "",
        qty: itemObj.qty ?? 1,
        rate: itemObj.rate ?? 0,
        discount: itemObj.discount ?? 0,
        GST: itemObj.GST ?? itemObj.gst ?? 12,
        ...itemObj,
      });
      return { ...prev, items };
    });
  };

  const handleSave = async () => {
    if (!editable?.voucherNo && !editable?.id) {
      alert("No voucher to save");
      return;
    }
    try {
      const { saveBillingFindVoucher } = await import("../services/billingImportSaveService");
      await saveBillingFindVoucher({
        voucherNo: editable.voucherNo || editable.id,
        items: (editable.items || []).map((it) => ({
          code: it.code || it.itemCode || "",
          desc: it.desc || it.itemName || "",
          qty: it.qty,
          rate: it.rate,
          discount: it.discount,
          gst: it.GST ?? it.gst,
          mrp: it.mrp,
          productId: it.productId,
        })),
        customerName: editable.party || editable.partyName || "",
        invoiceDate: editable.date || "",
        sourceRows: editable.sourceRows || [],
        sourceTemplateRow: editable.sourceTemplateRow || null,
        periodKey: editable.importPeriodKey || editable.periodKey || "",
      });
      alert("Saved to sales import ✅");
      onClose();
    } catch (err) {
      console.error("save error", err);
      alert(err?.message || "Failed to save to sales import ❌");
    }
  };

  const handlePrint = () => {
    const title = `Invoice - ${editable?.voucherNo || editable?.id || ""}`;
    const style = `<style>
      body{font-family:Arial,Helvetica,sans-serif;font-size:12px;margin:10px}
      table{width:100%;border-collapse:collapse}
      th,td{border:1px solid #444;padding:6px;font-size:12px}
      th{background:#f0f0f0}
      .right{text-align:right}
      .left{text-align:left}
    </style>`;
    const itemsHtml = (editable.items || []).map((it, i) => {
      const { discAmt, sgstAmt, cgstAmt, igstAmt, netAmount } = calcRow(it);
      return `<tr>
        <td>${i+1}</td>
        <td>${it.code||""}</td>
        <td>${it.desc||""}</td>
        <td class="right">${it.qty}</td>
        <td class="right">${it.rate}</td>
        <td class="right">${it.discount}</td>
        <td class="right">${discAmt.toFixed(2)}</td>
        <td class="right">${it.GST}</td>
        <td class="right">${isInterState ? igstAmt.toFixed(2) : sgstAmt.toFixed(2)}</td>
        <td class="right">${isInterState ? "" : cgstAmt.toFixed(2)}</td>
        <td class="right">${netAmount.toFixed(2)}</td>
      </tr>`;
    }).join("");

    const taxHeader = isInterState ? "<th>IGST</th><th></th>" : "<th>SGST</th><th>CGST</th>";
    const totalsTaxHtml = isInterState
      ? `<td>${totals.igst.toFixed(2)}</td><td></td>`
      : `<td>${totals.sgst.toFixed(2)}</td><td>${totals.cgst.toFixed(2)}</td>`;

    const html = `<html><head><title>${title}</title>${style}</head>
      <body><h3>${title}</h3>
      <div><b>Date:</b> ${editable.date}</div>
      <div><b>Party:</b> ${editable.party}</div>
      <table>
        <thead><tr>
          <th>Sl</th><th>Item</th><th>Description</th>
          <th>Qty</th><th>Rate</th><th>Disc%</th><th>Disc Amt</th>
          <th>GST%</th>${taxHeader}<th>Net</th>
        </tr></thead>
        <tbody>${itemsHtml}</tbody>
        <tfoot>
          <tr><td colspan="6">Totals</td>
            <td>${totals.discount.toFixed(2)}</td>
            <td></td>${totalsTaxHtml}
            <td>${totals.net.toFixed(2)}</td></tr>
          <tr><td colspan="10">Round Off</td><td>${roundOff.toFixed(2)}</td></tr>
          <tr><td colspan="10"><b>Grand Total</b></td><td><b>${grandTotalDisplay}</b></td></tr>
        </tfoot>
      </table></body></html>`;

    const w = window.open("", "_blank");
    if (!w) { alert("Popup blocked. Please allow popups."); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  // ------------------ Barcode lookup (simple) ------------------
  const handleBarcodeLookup = async () => {
    const code = (barcodeInput || "").toString().trim();
    if (!code) return alert("Enter barcode/code to lookup");
    try {
      // first try barcode field
      let q = query(collection(db, "Product Master Price List"), where("barcode", "==", code));
      let snap = await getDocs(q);
      if (snap.empty) {
        q = query(collection(db, "Product Master Price List"), where("part no", "==", code));
        snap = await getDocs(q);
      }
      if (!snap.empty) {
        const itemDoc = snap.docs[0].data();
        const partNo = itemDoc["part no"] || itemDoc.partno || itemDoc.code || code;
        addItemToEditable({
          code: partNo,
          desc: itemDoc.alias || itemDoc.desc || itemDoc["item name"] || "",
          rate: itemDoc.rate ?? itemDoc.mrp ?? 0,
          GST: itemDoc.GST ?? itemDoc.gst ?? 12,
          qty: 1,
        });
        setBarcodeInput("");
      } else {
        // Ask user to add a quick item
        const should = window.confirm("Item not found. Create new item with this barcode?");
        if (!should) return;
        const name = prompt("Item description/name:") || ("Item-" + Date.now());
        const rate = Number(prompt("Rate:", "0") || 0);
        const gst = Number(prompt("GST%:", "12") || 12);
        const part = (name.substring(0, 6) + Date.now()).toUpperCase();
        const payload = {
          "part no": part,
          alias: name,
          "item name": part,
          mrp: `$${rate.toFixed(2)}`,
          "cost price": `$${rate.toFixed(2)}`,
          barcode: code,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        const ref = await addDoc(collection(db, "Product Master Price List"), payload);
        const newItem = { id: ref.id, ...payload };
        addItemToEditable({ ...newItem, qty: 1 });
        setBarcodeInput("");
      }
    } catch (err) {
      console.error("barcode lookup error", err);
      alert("Barcode lookup failed. See console.");
    }
  };

  // ------------------ UI ------------------
  if (loading) return <div className="p-6 text-gray-600">⏳ Loading invoice...</div>;
  if (error) return <div className="p-6 text-red-600">{error}</div>;
  if (!editable) return <div className="p-6 text-red-600">Invoice not found</div>;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-3">
      <div className="bg-white rounded-2xl shadow-2xl w-full sm:w-[95%] max-w-[1100px] max-h-[95vh] overflow-hidden flex flex-col animate-fadeIn">
        {/* Header */}
        <div className="flex items-center justify-between bg-gradient-to-r from-gray-800 to-gray-700 text-white px-6 py-3">
          <div className="font-semibold text-lg">🧾 Invoice Details</div>
          <div className="flex gap-2">
            <button onClick={handlePrint} className="px-4 py-1.5 bg-green-600 hover:bg-green-700 rounded-lg text-sm font-medium shadow">🖨 Print</button>
            <button onClick={handleSave} className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium shadow">💾 Save</button>
            <button onClick={onClose} className="px-4 py-1.5 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-medium shadow">✖ Close</button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 overflow-auto flex-1 text-sm">
          {/* Top Info */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
            <div className="bg-gray-50 p-4 rounded-lg shadow-inner">
              <div><strong>Voucher No:</strong> {editable.voucherNo || editable.id}</div>
              <div className="mt-3">
                <label className="mr-2 font-medium">Date:</label>
                <input type="date" value={editable.date || ""} onChange={(e)=>handleInvoiceFieldChange("date", e.target.value)}
                  className="border rounded-lg px-3 py-1 text-sm shadow-sm focus:ring focus:ring-blue-200"/>
              </div>
              <div className="mt-3">
                <label className="font-medium mr-2">IGST (Inter-state):</label>
                <input type="checkbox" checked={isInterState} onChange={(e)=>setIsInterState(e.target.checked)} />
                <div className="text-xs text-gray-500 mt-1">Turn on if invoice is inter-state (IGST applied instead of CGST+SGST).</div>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg shadow-inner">
              <label className="block mb-2 font-medium">Party:</label>
              {originalPartyPresent ? (
                <div className="font-semibold">{editable.party || "(Not set)"}</div>
              ) : (
                <select value={editable.party || ""} onChange={(e)=>handleInvoiceFieldChange("party", e.target.value)}
                  className="border rounded-lg px-3 py-1 w-full shadow-sm focus:ring focus:ring-blue-200">
                  <option value="">-- Select Party --</option>
                  {parties.map((p) => <option key={p.id} value={p.partyName}>{p.partyName}</option>)}
                </select>
              )}
              <div className="text-xs text-gray-500 mt-1">
                {originalPartyPresent ? "Party taken from invoice (readonly)." : "Choose party from list then Save."}
              </div>
            </div>

            {/* Payment summary & edit */}
            <div className="bg-gray-50 p-4 rounded-lg shadow-inner">
              <div className="font-medium mb-2">Payment</div>
              <div className="flex gap-2 mb-2">
                <button className={`px-2 py-1 border rounded ${payment.mode==="Cash"?"bg-gray-200":""}`} onClick={()=>setPayment({...payment, mode:"Cash"})}>Cash</button>
                <button className={`px-2 py-1 border rounded ${payment.mode==="UPI"?"bg-gray-200":""}`} onClick={()=>setPayment({...payment, mode:"UPI"})}>UPI</button>
                <button className={`px-2 py-1 border rounded ${payment.mode==="Card"?"bg-gray-200":""}`} onClick={()=>setPayment({...payment, mode:"Card"})}>Card</button>
              </div>
              <div className="mb-2">
                <label className="text-xs">Amount Paid</label>
                <input type="number" value={payment.amountPaid || 0} onChange={(e)=>setPayment({...payment, amountPaid: Number(e.target.value)})}
                  className="w-full border rounded px-2 py-1"/>
              </div>
              {payment.mode==="UPI" && (
                <div className="mb-2">
                  <label className="text-xs">UPI ID</label>
                  <input value={payment.details?.upi||""} onChange={(e)=>setPayment({...payment, details:{...payment.details, upi:e.target.value}})}
                    className="w-full border rounded px-2 py-1"/>
                </div>
              )}
              {payment.mode==="Card" && (
                <div className="mb-2">
                  <label className="text-xs">Card last 4 digits</label>
                  <input value={payment.details?.cardLast4||""} onChange={(e)=>setPayment({...payment, details:{...payment.details, cardLast4:e.target.value}})}
                    className="w-full border rounded px-2 py-1"/>
                </div>
              )}
              <div className="text-xs text-gray-500">Payment stored with invoice on save.</div>
            </div>
          </div>

          {/* Barcode quick add */}
          <div className="mb-4 flex gap-2 items-center">
            <input placeholder="Paste barcode or item code and click Lookup" value={barcodeInput} onChange={(e)=>setBarcodeInput(e.target.value)}
              className="border px-3 py-2 rounded w-80 text-sm"/>
            <button onClick={handleBarcodeLookup} className="px-3 py-2 bg-blue-600 text-white rounded">Lookup & Add</button>
          </div>

          {/* Items Table */}
          <div className="overflow-x-auto rounded-lg border shadow">
            <table className="w-full text-xs border-collapse">
              <thead className="bg-gray-100 sticky top-0 shadow-sm">
                <tr>
                  {["Sl.","Item","Description","Qty","Rate","Discount %","Disc Amt","GST %", isInterState ? "IGST" : "SGST", isInterState ? "" : "CGST","Net Amt"].map((h,i) =>
                    <th key={i} className="border px-2 py-2 text-center font-medium">{h}</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y">
                {editable.items.map((it, idx) => {
                  const { discAmt, sgstAmt, cgstAmt, igstAmt, netAmount } = calcRow(it);
                  return (
                    <tr key={idx} className={`hover:bg-yellow-50 ${selectedRow===idx?"bg-yellow-100":""}`} onClick={()=>setSelectedRow(idx)}>
                      <td className="border px-2 py-2 text-center">{idx+1}</td>
                      <td className="border px-2 py-2">{it.code || it.itemName || "-"}</td>
                      <td className="border px-2 py-2">{it.desc}</td>
                      <td className="border px-2 py-2 text-right">
                        <input type="number" value={it.qty??""} onChange={(e)=>handleItemChange(idx,"qty",e.target.value)}
                          className="w-20 border rounded px-2 text-right focus:ring focus:ring-blue-200"/>
                      </td>
                      <td className="border px-2 py-2 text-right">
                        <input type="number" value={it.rate??""} onChange={(e)=>handleItemChange(idx,"rate",e.target.value)}
                          className="w-24 border rounded px-2 text-right focus:ring focus:ring-blue-200"/>
                      </td>
                      <td className="border px-2 py-2 text-right">
                        <input type="number" value={it.discount??0} onChange={(e)=>handleItemChange(idx,"discount",e.target.value)}
                          className="w-20 border rounded px-2 text-right focus:ring focus:ring-blue-200"/>
                      </td>
                      <td className="border px-2 py-2 text-right">{discAmt.toFixed(2)}</td>
                      <td className="border px-2 py-2 text-right">
                        <input type="number" value={it.GST??12} onChange={(e)=>handleItemChange(idx,"GST",e.target.value)}
                          className="w-16 border rounded px-2 text-right focus:ring focus:ring-blue-200"/>
                      </td>
                      <td className="border px-2 py-2 text-right">{isInterState ? igstAmt.toFixed(2) : sgstAmt.toFixed(2)}</td>
                      <td className="border px-2 py-2 text-right">{isInterState ? "" : cgstAmt.toFixed(2)}</td>
                      <td className="border px-2 py-2 text-right font-medium">{netAmount.toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr className="font-semibold">
                  <td colSpan={6} className="text-right px-2 py-2">Totals</td>
                  <td className="text-right px-2 py-2">{totals.discount.toFixed(2)}</td>
                  <td></td>
                  <td className="text-right px-2 py-2">{isInterState ? totals.igst.toFixed(2) : totals.sgst.toFixed(2)}</td>
                  <td className="text-right px-2 py-2">{!isInterState ? totals.cgst.toFixed(2) : ""}</td>
                  <td className="text-right px-2 py-2">{totals.net.toFixed(2)}</td>
                </tr>
                <tr>
                  <td colSpan={10} className="text-right px-2 py-2">Round Off</td>
                  <td className="text-right px-2 py-2">{roundOff.toFixed(2)}</td>
                </tr>
                <tr>
                  <td colSpan={10} className="text-right px-2 py-2 font-bold">Grand Total</td>
                  <td className="text-right px-2 py-2 font-bold text-lg">{grandTotalDisplay}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
